import { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Box, Button, Flex } from '@chakra-ui/react';

import { ApplicationState } from '../../../../store';
import FingerWithAction, { Status } from '../../../atoms/finger-with-action';
import { Section } from '../../../Finger/Section';
import VisaPrepAdditionalPersonalInfoStep1 from '../../../sections/visaPrep/additional-personal-info/step-1';
import VisaPrepAdditionalPersonalInfoStep2 from '../../../sections/visaPrep/additional-personal-info/step-2';
import VisaPrepAdditionalHistoryStep1Info from '../../../sections/visaPrep/additional-history/step-1';
import VisaPrepAdditionalHistoryStep2Info from '../../../sections/visaPrep/additional-history/step-2';
import VisaPrepAdditionalHistoryStep3Info from '../../../sections/visaPrep/additional-history/step-3';
import VisaPrepAdditionalHistoryStep4Info from '../../../sections/visaPrep/additional-history/step-4';
import VisaPrepAdditionalHistoryStep5Info from '../../../sections/visaPrep/additional-history/step-5';
import VisaPrepAdditionalHistoryStep6Info from '../../../sections/visaPrep/additional-history/step-6';
import VisaPrepAdditionalHistoryStep7Info from '../../../sections/visaPrep/additional-history/step-7';
import VisaPrepAdditionalHistoryStep8Info from '../../../sections/visaPrep/additional-history/step-8';
import VisaPrepFamilyStep1 from '../../../sections/visaPrep/family/step-1';
import VisaPrepFamilyStep2 from '../../../sections/visaPrep/family/step-2';
import VisaPrepFamilyStep3 from '../../../sections/visaPrep/family/step-3';
import VisaPrepFamilyStep4 from '../../../sections/visaPrep/family/step-4';
import VisaPrepFamilyStep5 from '../../../sections/visaPrep/family/step-5';
import VisaPrepOtherTravellersInfo from '../../../sections/visaPrep/other-travellers';
import VisaPrepArrivalInfo from '../../../sections/visaPrep/arrival';
import VisaPrepDeclarationsStep1 from '../../../sections/visaPrep/declarations';
import VisaPrepUniEnrolmentStep1 from '../../../sections/visaPrep/uni-enrolment/step-1';
import VisaPrepUniEnrolmentStep2 from '../../../sections/visaPrep/uni-enrolment/step-2';
import VisaPrepUniEnrolmentStep3 from '../../../sections/visaPrep/uni-enrolment/step-3';
import VisaPrepFeedbackInfo from '../../../sections/visaPrep/feedback';
import { hasPerm, P } from '../../../../utilities/roles/permissions';
import { visaPrepFlags } from '../../../../utilities/pages';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';
import { useTranslation } from "react-i18next";

const VISA_PREP_FORM = 'visa-prep-form';

interface OwnProps {
  id: any;
}

const VisaPrepForm = (props: OwnProps) => {
    const { t } = useTranslation('tables');
  // dispatch actions
  const { navigateTo } = useCustomNavigate();
  const onResume = (url: string) => navigateTo(`${props.id}/${url}`);

  // state props from store
  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);
  const auth = useSelector(({ auth }: ApplicationState) => auth, shallowEqual);
  const {
    nonHomeCountryApplication,
    hasTravelledToUk,
    hasLeaveToRemain,
    hasBorderRefusal,
    hasOtherTravellers,
    hasTakenEnglishTest,
  } = visaPrepFlags(student.personalInfo, student.offer, student.history);
  const userRole = auth.role;

  // constants
  const canResume = hasPerm(userRole, P.VISA_PREP_EDIT);

  // component state
  const [VPF_API_1, setVPF_API_1] = useState(Status.Todo);
  const [VPF_API_2, setVPF_API_2] = useState(Status.Todo);
  const [VPF_AH_1, setVPF_AH_1] = useState(Status.Todo);
  const [VPF_AH_2, setVPF_AH_2] = useState(Status.Todo);
  const [VPF_AH_3, setVPF_AH_3] = useState(Status.Todo);
  const [VPF_AH_4, setVPF_AH_4] = useState(Status.Todo);
  const [VPF_AH_5, setVPF_AH_5] = useState(Status.Todo);
  const [VPF_AH_6, setVPF_AH_6] = useState(Status.Todo);
  const [VPF_AH_7, setVPF_AH_7] = useState(Status.Todo);
  const [VPF_AH_8, setVPF_AH_8] = useState(Status.Todo);
  const [VPF_F_1, setVPF_F_1] = useState(Status.Todo);
  const [VPF_F_2, setVPF_F_2] = useState(Status.Todo);
  const [VPF_F_3, setVPF_F_3] = useState(Status.Todo);
  const [VPF_F_4, setVPF_F_4] = useState(Status.Todo);
  const [VPF_F_5, setVPF_F_5] = useState(Status.Todo);
  const [VPF_OT_1, setVPF_OT_1] = useState(Status.Todo);
  const [VPF_A_1, setVPF_A_1] = useState(Status.Todo);
  const [VPF_D_1, setVPF_D_1] = useState(Status.Todo);
  const [VPF_UE_1, setVPF_UE_1] = useState(Status.Todo);
  const [VPF_UE_2, setVPF_UE_2] = useState(Status.Todo);
  const [VPF_UE_3, setVPF_UE_3] = useState(Status.Todo);
  const [VPF_FB_1, setVPF_FB_1] = useState(Status.Todo);

  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (student && student.visa !== undefined) {
      const {
        isPage1Complete = false,
        isPage1_2Complete = false,
        isPage2_1Complete = false,
        isPage2_2Complete = false,
        isPage2_3Complete = false,
        isPage2_4Complete = false,
        isPage2_5Complete = false,
        isPage2_6Complete = false,
        isPage2_7Complete = false,
        isPage2_8Complete = false,
        isPage3_1Complete = false,
        isPage3_2Complete = false,
        isPage3_3Complete = false,
        isPage3_4Complete = false,
        isPage3_5Complete = false,
        isPage4Complete = false,
        isPage5Complete = false,
        isPage6Complete = false,
        isPage7_1Complete = false,
        isPage7_2Complete = false,
        isPage7_3Complete = false,
        isPage8Complete = false,
      } = student.visa;
      setVPF_API_1(isPage1Complete ? Status.Complete : Status.Todo);
      setVPF_API_2(isPage1_2Complete ? Status.Complete : isPage1Complete ? Status.InProgress : Status.Todo);
      setVPF_AH_1(
        isPage2_1Complete ? Status.Complete : isPage1Complete && isPage1_2Complete ? Status.InProgress : Status.Todo,
      );
      setVPF_AH_2(
        isPage2_2Complete
          ? Status.Complete
          : (nonHomeCountryApplication === true && isPage2_1Complete) ||
              (nonHomeCountryApplication === false && isPage1Complete)
            ? Status.InProgress
            : Status.Todo,
      );
      setVPF_AH_3(isPage2_3Complete ? Status.Complete : isPage2_2Complete ? Status.InProgress : Status.Todo);
      setVPF_AH_4(
        isPage2_4Complete
          ? Status.Complete
          : (hasTravelledToUk === true && isPage2_3Complete) || (hasTravelledToUk === false && isPage2_2Complete)
            ? Status.InProgress
            : Status.Todo,
      );
      setVPF_AH_5(isPage2_5Complete ? Status.Complete : isPage2_4Complete ? Status.InProgress : Status.Todo);
      setVPF_AH_6(
        isPage2_6Complete
          ? Status.Complete
          : (hasLeaveToRemain === true && isPage2_5Complete) || (hasLeaveToRemain === false && isPage2_4Complete)
            ? Status.InProgress
            : Status.Todo,
      );
      setVPF_AH_7(
        isPage2_7Complete
          ? Status.Complete
          : (hasBorderRefusal === true && isPage2_6Complete) ||
              (hasBorderRefusal === false && isPage2_5Complete) ||
              (hasBorderRefusal === false && hasLeaveToRemain === false && isPage2_4Complete)
            ? Status.InProgress
            : Status.Todo,
      );
      setVPF_AH_8(isPage2_8Complete ? Status.Complete : isPage2_7Complete ? Status.InProgress : Status.Todo);
      setVPF_F_1(isPage3_1Complete ? Status.Complete : isPage2_8Complete ? Status.InProgress : Status.Todo);
      setVPF_F_2(isPage3_2Complete ? Status.Complete : isPage3_1Complete ? Status.InProgress : Status.Todo);
      setVPF_F_3(isPage3_3Complete ? Status.Complete : isPage3_2Complete ? Status.InProgress : Status.Todo);
      setVPF_F_4(isPage3_4Complete ? Status.Complete : isPage3_3Complete ? Status.InProgress : Status.Todo);
      setVPF_F_5(isPage3_5Complete ? Status.Complete : isPage3_4Complete ? Status.InProgress : Status.Todo);
      setVPF_OT_1(isPage4Complete ? Status.Complete : isPage3_5Complete ? Status.InProgress : Status.Todo);
      setVPF_A_1(
        isPage5Complete
          ? Status.Complete
          : (hasOtherTravellers === true && isPage4Complete) || (hasOtherTravellers === false && isPage3_4Complete)
            ? Status.InProgress
            : Status.Todo,
      );
      setVPF_D_1(isPage6Complete ? Status.Complete : isPage5Complete ? Status.InProgress : Status.Todo);
      setVPF_UE_1(isPage7_1Complete ? Status.Complete : isPage6Complete ? Status.InProgress : Status.Todo);
      setVPF_UE_2(isPage7_2Complete ? Status.Complete : isPage7_1Complete ? Status.InProgress : Status.Todo);
      setVPF_UE_3(
        isPage7_3Complete
          ? Status.Complete
          : (hasTakenEnglishTest === false && isPage7_2Complete) || (hasTakenEnglishTest === true && isPage7_1Complete)
            ? Status.InProgress
            : Status.Todo,
      );
      setVPF_FB_1(isPage8Complete ? Status.Complete : isPage7_3Complete ? Status.InProgress : Status.Todo);
      setStarted(!!student.visa.isSectionStarted);
    }
  }, [
    hasBorderRefusal,
    hasLeaveToRemain,
    hasOtherTravellers,
    hasTakenEnglishTest,
    hasTravelledToUk,
    nonHomeCountryApplication,
    student,
  ]);

  return (
    <Box>
      {!started ? (
        <Flex>
          <Button
            id="save_and_continue"
            variant={'solid'}
            size={'lg'}
            onClick={() => (canResume ? onResume(`${VISA_PREP_FORM}/additional-personal-info/step-1`) : null)}
            flexGrow={1}
          >
            {t('tables:start')}
                                </Button>
        </Flex>
      ) : (
        <>
          <Section label={t('tables:section1AdditionalPersonalInform')}>
            <FingerWithAction
              id={'VPF_API_1'}
              status={VPF_API_1}
              // hasStart={!started && !complete}
              label={t('tables:passport')}
              onClick={() => onResume(`${VISA_PREP_FORM}/additional-personal-info/step-1`)}
              showAction={canResume}
            >
              <VisaPrepAdditionalPersonalInfoStep1 {...student.visa} />
            </FingerWithAction>
            <FingerWithAction
              id={'VPF_API_2'}
              status={VPF_API_2}
              label={t('tables:homeAddress')}
              onClick={() => onResume(`${VISA_PREP_FORM}/additional-personal-info/step-2`)}
              showAction={canResume}
            >
              <VisaPrepAdditionalPersonalInfoStep2 {...student.visa} />
            </FingerWithAction>
          </Section>
          <Section label={t('tables:section2AdditionalHistory')}>
            {nonHomeCountryApplication === true && (
              <FingerWithAction
                id={'VPF_AH_1'}
                status={VPF_AH_1}
                label={t('tables:applyingOutsideHomeCountry')}
                onClick={() => onResume(`${VISA_PREP_FORM}/additional-history/step-1`)}
                showAction={canResume}
              >
                <VisaPrepAdditionalHistoryStep1Info {...student.visa} />
              </FingerWithAction>
            )}
            <FingerWithAction
              id={'VPF_AH_2'}
              status={VPF_AH_2}
              label={t('tables:overseasVisas')}
              onClick={() => onResume(`${VISA_PREP_FORM}/additional-history/step-2`)}
              showAction={canResume}
            >
              <VisaPrepAdditionalHistoryStep2Info {...student.visa} />
            </FingerWithAction>
            {hasTravelledToUk === true && (
              <FingerWithAction
                id={'VPF_AH_3'}
                status={VPF_AH_3}
                label={t('tables:ukTravel')}
                onClick={() => onResume(`${VISA_PREP_FORM}/additional-history/step-3`)}
                showAction={canResume}
              >
                <VisaPrepAdditionalHistoryStep3Info {...student.visa} />
              </FingerWithAction>
            )}
            <FingerWithAction
              id={'VPF_AH_4'}
              status={VPF_AH_4}
              label={t('tables:previousUkActivities')}
              onClick={() => onResume(`${VISA_PREP_FORM}/additional-history/step-4`)}
              showAction={canResume}
            >
              <VisaPrepAdditionalHistoryStep4Info {...student.visa} />
            </FingerWithAction>
            {hasLeaveToRemain === true && (
              <FingerWithAction
                id={'VPF_AH_5'}
                status={VPF_AH_5}
                label={t('tables:leaveToRemain')}
                onClick={() => onResume(`${VISA_PREP_FORM}/additional-history/step-5`)}
                showAction={canResume}
              >
                <VisaPrepAdditionalHistoryStep5Info {...student.visa} />
              </FingerWithAction>
            )}
            {hasBorderRefusal === true && (
              <FingerWithAction
                id={'VPF_AH_6'}
                status={VPF_AH_6}
                label={t('tables:ukBorderRefusal')}
                onClick={() => onResume(`${VISA_PREP_FORM}/additional-history/step-6`)}
                showAction={canResume}
              >
                <VisaPrepAdditionalHistoryStep6Info {...student.visa} />
              </FingerWithAction>
            )}
            <FingerWithAction
              id={'VPF_AH_7'}
              status={VPF_AH_7}
              label={t('tables:deportationAndRemoval')}
              onClick={() => onResume(`${VISA_PREP_FORM}/additional-history/step-7`)}
              showAction={canResume}
            >
              <VisaPrepAdditionalHistoryStep7Info {...student.visa} />
            </FingerWithAction>
            <FingerWithAction
              id={'VPF_AH_8'}
              status={VPF_AH_8}
              label={t('tables:travelExcludingTheUk')}
              onClick={() => onResume(`${VISA_PREP_FORM}/additional-history/step-8`)}
              showAction={canResume}
            >
              <VisaPrepAdditionalHistoryStep8Info {...student.visa} />
            </FingerWithAction>
          </Section>
          <Section label={t('tables:section3Family')}>
            <FingerWithAction
              id={'VPF_F_1'}
              status={VPF_F_1}
              label={t('tables:spouseOrPartnerDetails')}
              onClick={() => onResume(`${VISA_PREP_FORM}/family/step-1`)}
              showAction={canResume}
            >
              <VisaPrepFamilyStep1 {...student.visa} />
            </FingerWithAction>
            <FingerWithAction
              id={'VPF_F_2'}
              status={VPF_F_2}
              label={t('tables:fatherSDetails')}
              onClick={() => onResume(`${VISA_PREP_FORM}/family/step-2`)}
              showAction={canResume}
            >
              <VisaPrepFamilyStep2 {...student.visa} />
            </FingerWithAction>
            <FingerWithAction
              id={'VPF_F_3'}
              status={VPF_F_3}
              label={t('tables:motherSDetails')}
              onClick={() => onResume(`${VISA_PREP_FORM}/family/step-3`)}
              showAction={canResume}
            >
              <VisaPrepFamilyStep3 {...student.visa} />
            </FingerWithAction>
            <FingerWithAction
              id={'VPF_F_4'}
              status={VPF_F_4}
              label={t('tables:guardianDetails')}
              onClick={() => onResume(`${VISA_PREP_FORM}/family/step-4`)}
              showAction={canResume}
            >
              <VisaPrepFamilyStep4 {...student.visa} />
            </FingerWithAction>
            <FingerWithAction
              id={'VPF_F_5'}
              status={VPF_F_5}
              label={t('tables:dependantChildren')}
              onClick={() => onResume(`${VISA_PREP_FORM}/family/step-5`)}
              showAction={canResume}
            >
              <VisaPrepFamilyStep5 {...student.visa} />
            </FingerWithAction>
          </Section>
          {hasOtherTravellers && (
            <Section label={t('tables:section4OtherTravellers')}>
              <FingerWithAction
                id={'VPF_OT_1'}
                status={VPF_OT_1}
                label={t('tables:travellerDetails')}
                onClick={() => onResume(`${VISA_PREP_FORM}/other-travellers/step-1`)}
                showAction={canResume}
              >
                <VisaPrepOtherTravellersInfo {...student.visa} />
              </FingerWithAction>
            </Section>
          )}
          <Section label={t('tables:section5Arrival')}>
            <FingerWithAction
              id={'VPF_A_1'}
              status={VPF_A_1}
              label={t('tables:ukContactsDetails')}
              onClick={() => onResume(`${VISA_PREP_FORM}/arrival/step-1`)}
              showAction={canResume}
            >
              <VisaPrepArrivalInfo {...student.visa} />
            </FingerWithAction>
          </Section>
          <Section label={t('tables:section6Declarations')}>
            <FingerWithAction
              id={'VPF_D_1'}
              status={VPF_D_1}
              label={t('tables:declarations')}
              onClick={() => onResume(`${VISA_PREP_FORM}/declarations/step-1`)}
              showAction={canResume}
            >
              <VisaPrepDeclarationsStep1 {...student.visa} />
            </FingerWithAction>
          </Section>
          <Section label={t('tables:section7UniversityEnrolment')}>
            <FingerWithAction
              id={'VPF_UE_1'}
              status={VPF_UE_1}
              label={t('tables:casAndCourse')}
              onClick={() => onResume(`${VISA_PREP_FORM}/uni-enrolment/step-1`)}
              showAction={canResume}
            >
              <VisaPrepUniEnrolmentStep1 {...student.visa} />
            </FingerWithAction>
            {hasTakenEnglishTest === false && (
              <FingerWithAction
                id={'VPF_UE_2'}
                status={VPF_UE_2}
                label={t('tables:englishTest')}
                onClick={() => onResume(`${VISA_PREP_FORM}/uni-enrolment/step-2`)}
                showAction={canResume}
              >
                <VisaPrepUniEnrolmentStep2 {...student.visa} />
              </FingerWithAction>
            )}
            <FingerWithAction
              id={'VPF_UE_3'}
              status={VPF_UE_3}
              label={t('tables:ucas')}
              onClick={() => onResume(`${VISA_PREP_FORM}/uni-enrolment/step-3`)}
              showAction={canResume}
            >
              <VisaPrepUniEnrolmentStep3 {...student.visa} />
            </FingerWithAction>
          </Section>
          <Section label={t('tables:section8Feedback')}>
            <FingerWithAction
              id={'VPF_FB_1'}
              status={VPF_FB_1}
              label={t('tables:feedback')}
              onClick={() => onResume(`${VISA_PREP_FORM}/feedback/step-1`)}
              showAction={canResume}
            >
              <VisaPrepFeedbackInfo {...student.visa} />
            </FingerWithAction>
          </Section>
        </>
      )}
    </Box>
  );
};

export default VisaPrepForm;
